import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ArrowRight } from "lucide-react";
import { triggerHaptic } from "@/utils/haptics";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  BusinessLoanPage,
  ContactPage,
  InterestRateCalculatorPage,
  KlarsprakPage,
  LoanWithoutUCPage,
  MortgageCalculatorPage,
  MortgagePage,
  PrivateLoanCalculatorPage,
  PrivateLoanPage,
} from "@/routes/preloadableRoutes";

type MobileNavItem = {
  label: string;
  path: string;
  description?: string;
  preload?: () => void;
};

interface MobileNavSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const MobileNavSheet = ({ open, onOpenChange }: MobileNavSheetProps) => {
  const { currentLanguage } = useLanguage();

  const comparisonLinks: MobileNavItem[] = [
    { label: "Privatlån", path: `/${currentLanguage}/privatlan`, description: "Jämför långivare", preload: PrivateLoanPage.preload },
    { label: "Bolån", path: `/${currentLanguage}/bolan`, description: "Räntor och banker", preload: MortgagePage.preload },
    { label: "Lån utan UC", path: `/${currentLanguage}/lana-utan-uc`, description: "Alternativa kreditupplysningar", preload: LoanWithoutUCPage.preload },
    { label: "Företagslån", path: `/${currentLanguage}/foretagslan`, description: "Finansiering för bolag", preload: BusinessLoanPage.preload },
  ];

  const supportLinks: MobileNavItem[] = [
    { label: "Guider", path: `/${currentLanguage}/klarsprak`, preload: KlarsprakPage.preload },
    { label: "Artiklar", path: `/${currentLanguage}/blog` },
    { label: "Privatlånekalkylator", path: `/${currentLanguage}/verktyg/privatlanekalkylator`, preload: PrivateLoanCalculatorPage.preload },
    { label: "Bolånekalkylator", path: `/${currentLanguage}/verktyg/bolanekalkylator`, preload: MortgageCalculatorPage.preload },
    { label: "Räntekalkylator", path: `/${currentLanguage}/verktyg/rantekalkylator`, preload: InterestRateCalculatorPage.preload },
  ];

  const footerLinks: MobileNavItem[] = [
    { label: "Vanliga frågor", path: `/${currentLanguage}/faq` },
    { label: "Redaktionen", path: `/${currentLanguage}/redaktionen` },
    { label: "Kontakt", path: `/${currentLanguage}/kontakt`, preload: ContactPage.preload },
  ];

  const handleItemClick = (path: string) => {
    triggerHaptic('light');
    onOpenChange(false);
  };

  const renderComparisonItem = (item: MobileNavItem) => (
    <Link
      key={item.path}
      to={item.path}
      onClick={() => handleItemClick(item.path)}
      onFocus={() => item.preload?.()}
      onMouseEnter={() => item.preload?.()}
      onTouchStart={() => item.preload?.()}
      className="group flex min-h-[72px] w-full flex-col justify-between rounded-lg border bg-card p-3 text-left touch-manipulation"
    >
      <span className="block text-sm font-semibold leading-tight text-foreground">{item.label}</span>
      <span className="mt-2 flex items-center justify-between gap-2 text-xs leading-5 text-muted-foreground">
        <span>{item.description}</span>
        <ArrowRight className="h-4 w-4 flex-shrink-0 text-primary" />
      </span>
    </Link>
  );

  const renderListItem = (item: MobileNavItem) => (
    <Link
      key={item.path}
      to={item.path}
      onClick={() => handleItemClick(item.path)}
      onFocus={() => item.preload?.()}
      onMouseEnter={() => item.preload?.()}
      onTouchStart={() => item.preload?.()}
      className="group flex min-h-[52px] w-full items-center justify-between gap-3 px-4 py-3 text-left touch-manipulation"
    >
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold leading-tight text-foreground">{item.label}</span>
      </span>
      <ArrowRight className="h-4 w-4 flex-shrink-0 text-primary" />
    </Link>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent id="mobile-navigation-menu" side="right" className="flex w-full flex-col bg-surface p-0 sm:max-w-md">
        <SheetHeader className="border-b p-5 pb-4 text-left">
          <SheetTitle className="text-left text-lg font-bold">Hitta rätt</SheetTitle>
          <SheetDescription className="text-sm">Välj jämförelse eller fortsätt läsa.</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="space-y-6 p-5">
            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Jämför
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {comparisonLinks.map((item) => renderComparisonItem(item))}
              </div>
            </div>

            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Förstå och räkna
              </h3>
              <div className="divide-y rounded-lg border bg-card">
                {supportLinks.map((item) => renderListItem(item))}
              </div>
            </div>

            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Om Klarfinans
              </h3>
              <div className="divide-y rounded-lg border bg-card">
                {footerLinks.map((item) => renderListItem(item))}
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
